import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import cmsContentBlockAssignmentObject from '@/entities/cmsContentBlockAssignment/interface'

import {
  ALL_PAGES_QUERY,
  SINGLE_PAGE_QUERY,
  PAGES_DEPENDENTS_QUERY,
  CREATE_PAGE_MUTATION,
  UPDATE_PAGE_MUTATION,
  DELETE_PAGE_MUTATION,
  FORCE_DELETE_PAGE_MUTATION,
  ADD_PAGE_CONTENT_BLOCK_ASSIGNMENTS_MUTATION,
  REMOVE_PAGE_CONTENT_BLOCK_ASSIGNMENTS_MUTATION,
  CREATE_PAGE_CONTENT_BLOCK_ASSIGNMENTS_MUTATION,
  DELETE_PAGE_CONTENT_BLOCK_ASSIGNMENTS_MUTATION,
  UPDATE_PAGE_CONTENT_BLOCK_ASSIGNMENTS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsPageRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_PAGE_QUERY, variables).then((data: any) => {
      return hydrateObject(data.page)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_PAGES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.pages)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_PAGE_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.page),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_PAGES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.pages),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(PAGES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([PAGES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_PAGE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.page)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_PAGE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.page)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_PAGE_MUTATION, variables).then((data: any) => {
      return data.deleteCmsPage
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_PAGE_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsPage
    })
  },

  assignContentBlockAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PAGE_CONTENT_BLOCK_ASSIGNMENTS_MUTATION, variables)
  },

  unassignContentBlockAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PAGE_CONTENT_BLOCK_ASSIGNMENTS_MUTATION, variables)
  },

  createContentBlockAssignments: async (id: string, items: Array<cmsContentBlockAssignmentObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignments: items,
      },
    }
    return graphQLClient.request(CREATE_PAGE_CONTENT_BLOCK_ASSIGNMENTS_MUTATION, variables)
  },

  deleteContentBlockAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PAGE_CONTENT_BLOCK_ASSIGNMENTS_MUTATION, variables)
  },

  updateContentBlockAssignmentsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_PAGE_CONTENT_BLOCK_ASSIGNMENTS_ORDER_MUTATION, variables)
  },
}

export default cmsPageRepository
